import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IContentListResponse } from 'src/app/core/models/i-content-list-response';
import { AppConfig } from 'src/app/app-config';
import { IContentList } from 'src/app/core/models/i-content-list';
import { CreateContentListRequest } from '../../create-content-list-request';
import { IUpdateContentList } from '../../../../core/models/i-update-content-list';
import { DeleteContentListRequest } from '../../delete-content-list-request';
import { IContentResults } from '../../../../core/models/i-content';
import { RestService } from 'src/app/shared/services/rest-service';
import { HttpResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ContentListService {
  static readonly ENTITY_IDENTIFIER: string = 'content-lists';
  private readonly BASE_CONTENT_LIST_ROUTE = `api/${ContentListService.ENTITY_IDENTIFIER}`;
  contentResultLimit: number;
  private readonly BUILD_HTTP_ROUTE = (...args) =>
    this.restService.BUILD_HTTP_ROUTE(this.BASE_CONTENT_LIST_ROUTE, ...args);

  constructor(private restService: RestService, private appConfig: AppConfig) {
    this.contentResultLimit = this.appConfig.contentResultLimit;
  }

  // TODO IMPROVEMENT: Could be just a wrapper of getFilterd$ with no filters.
  getAll$(): Observable<IContentListResponse> {
    return this.restService.get$<IContentListResponse>(this.BUILD_HTTP_ROUTE());
  }

  getFiltered$(
    filterParams: IContentList,
    pageNumber = '0',
    pageSize = '100'
  ): Observable<IContentListResponse> {
    const params = this.restService.buildParamsObjectWithPaging(
      {
        searchText: filterParams.name ?? '',
        contentListType: filterParams.contentListType ?? '',
        deliveryTargetCodes: filterParams.website.join(','),
      },
      +pageNumber,
      +pageSize
    );
    return this.restService.get$<IContentListResponse>(
      this.BUILD_HTTP_ROUTE(),
      { params }
    );
  }

  getSuggested$(
    search: string = null,
    websites: string[] = null,
    topics: string[] = null,
    tags: string[] = null
  ): Observable<IContentList[]> {
    const params = this.restService.buildHttpParamsObject({
      searchtext: search,
      deliveryTargetCodes: websites,
      topicIds: topics,
      tagIds: tags,
    });

    return this.restService.get$<IContentList[]>(
      this.BUILD_HTTP_ROUTE('suggestions'),
      {
        params,
      }
    );
  }

  create$(request: CreateContentListRequest): Observable<boolean> {
    return this.restService.post$<boolean>(this.BUILD_HTTP_ROUTE(), request);
  }

  update$(
    updateContentListRequest: IUpdateContentList,
    contentListId: string
  ): Observable<void> {
    return this.restService.put$<void>(
      this.BUILD_HTTP_ROUTE(contentListId),
      updateContentListRequest
    );
  }

  delete$(contentListId: string): Observable<boolean> {
    return this.restService.remove$(this.BUILD_HTTP_ROUTE(contentListId));
  }

  contentListExistsByName$(name: string): Observable<HttpResponse<any> | null> {
    return this.restService.head$<HttpResponse<any>>(
      this.BUILD_HTTP_ROUTE(name),
      {
        observe: 'response',
      }
    );
  }

  getById$(id: string): Observable<IContentList> {
    return this.restService.get$<IContentList>(this.BUILD_HTTP_ROUTE(id));
  }

  getContent$(
    contentListCode: string,
    pageSize: number = this.contentResultLimit,
    pageNumber: number = 0
  ): Observable<IContentResults> {
    const params = this.restService.buildParamsObjectWithPaging(
      {},
      pageNumber,
      pageSize
    );
    return this.restService.get$<IContentResults>(
      this.BUILD_HTTP_ROUTE(contentListCode, 'contents'),
      { params }
    );
  }

  getTypes$(): string[] {
    const contentListTypeList: string[] = [
      'CoreList',
      'DefaultList',
      'PickList',
    ];
    return contentListTypeList;
  }
}
